import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Building2,
  Search,
  Plus,
  KeyRound,
  MapPin,
  Users,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Clock,
  Home,
  Send,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BuildingOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState('choice'); // choice, search, invite-code, request-sent
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [accessMessage, setAccessMessage] = useState('');
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const token = localStorage.getItem('session_token');

  useEffect(() => {
    if (token) {
      checkOnboardingStatus();
    }
  }, [token]);

  const checkOnboardingStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/building-discovery/onboarding-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOnboardingStatus(data);
        // Multi-building support: do NOT auto-redirect existing users away.
        // They may have arrived here via "Add another building". They can use
        // the back button or the active-building banner to return.
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const redirectToDashboard = () => {
    if (user?.role === 'managing_agent' || user?.role === 'agent_admin') {
      navigate('/agent');
    } else if (user?.role === 'trustee') {
      navigate('/trustee-hub');
    } else {
      navigate('/dashboard');
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(
        `${API_URL}/api/building-discovery/search?query=${encodeURIComponent(searchQuery)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
        if (data.results.length === 0) {
          toast.info('No buildings found. Try a different search or create a new building.');
        }
      } else {
        toast.error('Failed to search buildings');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search buildings');
    } finally {
      setSearching(false);
    }
  };

  const handleRequestAccess = async (building) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/building-discovery/request-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          building_id: building.building_id,
          message: accessMessage || `I would like to join ${building.name}`
        })
      });

      if (response.ok) {
        setSelectedBuilding(building);
        setStep('request-sent');
        toast.success(`You're in! Welcome to ${building.name}.`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send request');
      }
    } catch (error) {
      console.error('Request error:', error);
      toast.error('Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWithCode = async () => {
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/building-discovery/join-with-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invite_code: inviteCode.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Successfully joined ${data.building_name}!`);
        // Redirect to dashboard
        setTimeout(() => redirectToDashboard(), 1500);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid invite code');
      }
    } catch (error) {
      console.error('Join error:', error);
      toast.error('Failed to join building');
    } finally {
      setLoading(false);
    }
  };

  // Choice Screen
  if (step === 'choice') {
    const hasExistingBuildings = onboardingStatus?.onboarding_complete || onboardingStatus?.has_building;
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {hasExistingBuildings && (
            <div className="mb-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/10"
                onClick={() => navigate(-1)}
                data-testid="onboarding-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </div>
          )}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {hasExistingBuildings ? 'Add Another Building' : 'Welcome to BodyCorp365'}
            </h1>
            <p className="text-blue-100">How would you like to continue?</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Find My Building */}
            <Card 
              className="bg-white/10 backdrop-blur border-white/20 cursor-pointer hover:bg-white/20 transition-all"
              onClick={() => setStep('search')}
              data-testid="find-building-card"
            >
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Search className="w-7 h-7 text-blue-300" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Find My Building</h3>
                <p className="text-blue-200 text-sm">
                  Search for your building and request access to join
                </p>
              </CardContent>
            </Card>

            {/* Create New Building */}
            <Card 
              className="bg-white/10 backdrop-blur border-white/20 cursor-pointer hover:bg-white/20 transition-all"
              onClick={() => navigate('/create-building')}
              data-testid="create-building-card"
            >
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Plus className="w-7 h-7 text-emerald-300" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Create New Building</h3>
                <p className="text-blue-200 text-sm">
                  Register your building if it's not yet listed
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Have Invite Code */}
          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              className="text-blue-200 hover:text-white hover:bg-white/10"
              onClick={() => setStep('invite-code')}
            >
              <KeyRound className="w-4 h-4 mr-2" />
              I have an invite code
            </Button>
          </div>

          {/* Pending Requests Notice */}
          {onboardingStatus?.pending_requests > 0 && (
            <Card className="mt-6 bg-amber-500/20 border-amber-500/30">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="text-white font-medium">
                    You have {onboardingStatus.pending_requests} pending request(s)
                  </p>
                  <p className="text-amber-200 text-sm">
                    Waiting for building admin approval
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Search Screen
  if (step === 'search') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <Button
            variant="ghost"
            className="text-white mb-4"
            onClick={() => setStep('choice')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Card className="bg-white shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-600" />
                Find My Building
              </CardTitle>
              <CardDescription>
                Search by building name or address
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search Input */}
              <div className="flex gap-2">
                <Input
                  placeholder="Enter building name or address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="building-search-input"
                />
                <Button onClick={handleSearch} disabled={searching}>
                  {searching ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-3 mt-4">
                  <Label className="text-sm text-slate-500">
                    {searchResults.length} building(s) found
                  </Label>
                  {searchResults.map((building) => (
                    <Card 
                      key={building.building_id} 
                      className="border hover:border-blue-300 transition-all"
                      data-testid={`building-result-${building.building_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900">{building.name}</h4>
                            <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                              <MapPin className="w-3 h-3" />
                              <span>{building.city || building.address || 'Location not specified'}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Home className="w-3 h-3" />
                                {building.building_type}
                              </span>
                              {building.total_units > 0 && (
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {building.total_units} units
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleRequestAccess(building)}
                            disabled={loading}
                            data-testid={`request-access-${building.building_id}`}
                          >
                            {loading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-3 h-3 mr-1" />
                                Join Building
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* No Results */}
              {searchResults.length === 0 && searchQuery && !searching && (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No buildings found</p>
                  <Button
                    variant="link"
                    className="mt-2"
                    onClick={() => navigate('/create-building')}
                  >
                    Create a new building instead
                  </Button>
                </div>
              )}

              {/* Have Invite Code Link */}
              <div className="pt-4 border-t text-center">
                <Button
                  variant="ghost"
                  className="text-slate-500"
                  onClick={() => setStep('invite-code')}
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Have an invite code?
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Invite Code Screen
  if (step === 'invite-code') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-xl">
          <CardHeader className="text-center">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <KeyRound className="w-7 h-7 text-blue-600" />
            </div>
            <CardTitle>Enter Invite Code</CardTitle>
            <CardDescription>
              Enter the invite code shared by your building admin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Invite Code</Label>
              <Input
                placeholder="e.g., BC365-OV123"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                className="text-center text-lg font-mono tracking-wider"
                data-testid="invite-code-input"
              />
            </div>

            <Button
              className="w-full"
              onClick={handleJoinWithCode}
              disabled={loading || !inviteCode.trim()}
              data-testid="join-with-code-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Join Building
                </>
              )}
            </Button>

            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setStep('choice')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Request Sent Screen
  if (step === 'request-sent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-xl text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">You're In!</h2>
            <p className="text-slate-600 mb-6">
              You've been added to <strong>{selectedBuilding?.name}</strong>. The building managers have been notified and you can start using the app right away.
            </p>
            <div className="bg-emerald-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Instant access granted</span>
              </div>
              <p className="text-sm text-emerald-600 mt-1">
                Your account manager can adjust your access anytime.
              </p>
            </div>

            <div className="space-y-2">
              <Button
                className="w-full"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setStep('choice')}
              >
                Join Another Building
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
